import React, { useState, useEffect } from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import { ActivityIndicator, Text, TextInput } from 'react-native-paper';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useAppColors } from '../../hooks/useAppColors';
import { useLogin } from '../../hooks/useAuth';
import { getApiError } from '../../utils/formatters';
import { testNetworkConnectivity, getDeviceInfo } from '../../utils/networkTest';
import { useTranslation } from 'react-i18next';
import LoadingIndicator from '../../components/common/LoadingIndicator';
import AuthLayout from './AuthLayout';
import GoogleSignInButton from './GoogleSignInButton';
import AppleSignInButton from './AppleSignInButton';
import AuthDivider from './AuthDivider';

export default function LoginScreen({ navigation }: any) {
  const { t } = useTranslation();
  const colors = useAppColors();
  const insets = useSafeAreaInsets();
  const loginMutation = useLogin();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [secureEntry, setSecureEntry] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    getDeviceInfo();
    testNetworkConnectivity();
  }, []);

  const handleLogin = () => {
    const newErrors: Record<string, string> = {};
    if (!email.trim()) newErrors.email = t('auth.emailRequired');
    if (!password.trim()) newErrors.password = t('auth.passwordRequired');
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});
    loginMutation.mutate(
      { email: email.trim(), password },
      {
        onSuccess: (response) => {
          if (!response.emailVerified) {
            // User needs to verify email first
            navigation.navigate('EmailVerification', { email: response.email });
          }
          // If verified, useLogin's onSuccess already sets auth — RootNavigator auto-switches
        },
      },
    );
  };

  return (
    <>
    <AuthLayout
      title={t('auth.welcomeBack')}
      subtitle={t('auth.signInSubtitle')}
      useKeyboardAvoiding
    >
          <GoogleSignInButton />

          <AppleSignInButton />

          <AuthDivider />

          <TextInput
            label={t('auth.email')}
            value={email}
            onChangeText={(v) => {
              setEmail(v);
              if (errors.email) setErrors((e) => ({ ...e, email: '' }));
            }}
            mode="outlined"
            keyboardType="email-address"
            autoCapitalize="none"
            autoComplete="email"
            error={!!errors.email}
            style={styles.input}
            outlineColor={colors.border}
            activeOutlineColor={colors.text}
            textColor={colors.text}
            theme={{
              colors: {
                onSurfaceVariant: colors.textSecondary,
                surface: colors.card,
              },
            }}
          />
          {errors.email && (
            <Text style={[styles.fieldError, { color: colors.error }]}>
              {errors.email}
            </Text>
          )}

          <TextInput
            label={t('auth.password')}
            value={password}
            onChangeText={(v) => {
              setPassword(v);
              if (errors.password) setErrors((e) => ({ ...e, password: '' }));
            }}
            mode="outlined"
            secureTextEntry={secureEntry}
            error={!!errors.password}
            right={
              <TextInput.Icon
                icon={secureEntry ? 'eye-off' : 'eye'}
                onPress={() => setSecureEntry(!secureEntry)}
                color={colors.textTertiary}
              />
            }
            style={styles.input}
            outlineColor={colors.border}
            activeOutlineColor={colors.text}
            textColor={colors.text}
            theme={{
              colors: {
                onSurfaceVariant: colors.textSecondary,
                surface: colors.card,
              },
            }}
          />
          {errors.password && (
            <Text style={[styles.fieldError, { color: colors.error }]}>
              {errors.password}
            </Text>
          )}

          {loginMutation.isError && (
            <Text style={[styles.error, { color: colors.error }]}>
              {getApiError(loginMutation.error, t('auth.loginFailed'), t)}
            </Text>
          )}

          <TouchableOpacity
            onPress={handleLogin}
            disabled={loginMutation.isPending}
            style={[styles.button, { backgroundColor: colors.accent }]}
            activeOpacity={0.8}
          >
            {loginMutation.isPending ? (
              <ActivityIndicator size="small" color={colors.accentText} />
            ) : (
              <Text style={[styles.buttonText, { color: colors.accentText }]}>
                {t('auth.signIn')}
              </Text>
            )}
          </TouchableOpacity>

          <TouchableOpacity
            onPress={() => navigation.navigate('ForgotPassword')}
            style={styles.forgotButton}
            activeOpacity={0.7}
          >
            <Text style={[styles.linkText, { color: colors.textSecondary }]}>
              {t('auth.forgotPassword')}
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            onPress={() => navigation.navigate('Register')}
            style={styles.linkButton}
            activeOpacity={0.7}
          >
            <Text style={[styles.linkText, { color: colors.textSecondary }]}>
              {t('auth.noAccount')}{' '}
              <Text style={{ color: colors.text, fontWeight: '600' }}>
                {t('auth.register')}
              </Text>
            </Text>
          </TouchableOpacity>
    </AuthLayout>

    <View style={[styles.appDescriptionContainer, { bottom: 48 + insets.bottom }]}>
      <Text style={[styles.appDescription, { color: colors.textTertiary }]}>
        {t('auth.appDescription')}
      </Text>
    </View>

    <View style={[styles.legalLinksRow, { bottom: 16 + insets.bottom }]}>
      <Text style={[styles.legalLinksText, { color: colors.textSecondary }]}>
        <Text
          style={{ color: colors.accent }}
          onPress={() => navigation.navigate('PublicLegalDoc', { docType: 'privacy_policy' })}
        >
          {t('auth.privacyPolicy')}
        </Text>
        {'  ·  '}
        <Text
          style={{ color: colors.accent }}
          onPress={() => navigation.navigate('PublicLegalDoc', { docType: 'terms_of_service' })}
        >
          {t('auth.termsOfService')}
        </Text>
      </Text>
    </View>
  </>
  );
}

const styles = StyleSheet.create({
  appDescriptionContainer: {
    position: 'absolute',
    bottom: 48,
    left: 0,
    right: 0,
    alignItems: 'center',
    paddingHorizontal: 32,
  },
  appDescription: {
    fontSize: 13,
    textAlign: 'center',
  },
  input: {
    marginBottom: 12,
    backgroundColor: 'transparent',
  },
  error: {
    textAlign: 'center',
    marginBottom: 12,
    fontSize: 13,
  },
  fieldError: {
    marginTop: -8,
    marginBottom: 8,
    marginLeft: 4,
    fontSize: 12,
  },
  button: {
    marginTop: 8,
    paddingVertical: 14,
    borderRadius: 12,
    alignItems: 'center',
  },
  buttonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  linkButton: {
    marginTop: 20,
    alignItems: 'center',
  },
  linkText: {
    fontSize: 14,
  },
  forgotButton: {
    marginTop: 12,
    alignItems: 'flex-end',
  },
  dividerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: 20,
  },
  dividerLine: {
    flex: 1,
    height: 1,
  },
  dividerText: {
    marginHorizontal: 12,
    fontSize: 13,
  },
  socialButton: {
    paddingVertical: 14,
    borderRadius: 12,
    borderWidth: 1,
    alignItems: 'center',
  },
  socialButtonText: {
    fontSize: 16,
    fontWeight: '500',
  },
  legalLinksRow: {
    position: 'absolute',
    bottom: 16,
    left: 0,
    right: 0,
    alignItems: 'center',
  },
  legalLinksText: {
    fontSize: 13,
  },
});
